import raw from './personalization.json';

/**
 * Personal content is DEVICE-LOCAL user data, not shipped in the bundle.
 * `personalization.json` holds only neutral defaults; the owner enters real
 * details on first launch and they live in localStorage on the device — nothing
 * personal ever reaches the public URL. The photo is a separate local data-URL.
 */
export interface PetConfig {
  type: string;
  name: string;
  emoji: string;
}
export interface ClaraConfig {
  name: string;
  phrases: string[];
}
export interface Birthday {
  month: number; // 1–12
  day: number; // 1–31
}
export interface Personalization {
  farmName: string;
  playerDefaultName: string;
  pet: PetConfig;
  clara: ClaraConfig;
  signText: string;
  greeting: string;
  letters: string[];
  birthday: Birthday | null;
}

/** Flat, device-local profile the owner sets at first run. */
export interface StoredProfile {
  farmName?: string;
  playerDefaultName?: string;
  claraName?: string;
  birthday?: Birthday | null;
}

const PROFILE_KEY = 'sandyhollow.profile';
const PHOTO_KEY = 'sandyhollow.photo';

function localProfile(): StoredProfile {
  try {
    const j = localStorage.getItem(PROFILE_KEY);
    return j ? (JSON.parse(j) as StoredProfile) : {};
  } catch {
    return {};
  }
}

/** True once the owner has completed first-run setup on this device. */
export function hasProfile(): boolean {
  return Object.keys(localProfile()).length > 0;
}

/** Re-merge device-local details over the shipped neutral defaults, in place. */
export function refreshPersonalization(): void {
  const base = raw as Personalization;
  const p = localProfile();
  personalization.farmName = p.farmName ?? base.farmName;
  personalization.playerDefaultName = p.playerDefaultName ?? base.playerDefaultName;
  personalization.signText = p.farmName ?? base.signText;
  personalization.greeting = base.greeting;
  personalization.letters = base.letters;
  personalization.pet = base.pet;
  personalization.clara = { name: p.claraName ?? base.clara.name, phrases: base.clara.phrases };
  personalization.birthday = p.birthday ?? base.birthday ?? null;
}

/** Persist profile fields locally (device-only) and re-merge. */
export function saveProfile(p: StoredProfile): void {
  try {
    localStorage.setItem(PROFILE_KEY, JSON.stringify({ ...localProfile(), ...p }));
  } catch {
    /* ignore */
  }
  refreshPersonalization();
}
export function getProfile(): StoredProfile {
  return localProfile();
}

export function getLocalPhoto(): string | null {
  try {
    return localStorage.getItem(PHOTO_KEY);
  } catch {
    return null;
  }
}
export function setLocalPhoto(dataUrl: string): void {
  try {
    localStorage.setItem(PHOTO_KEY, dataUrl);
  } catch {
    /* ignore */
  }
}
export function clearLocalPhoto(): void {
  try {
    localStorage.removeItem(PHOTO_KEY);
  } catch {
    /* ignore */
  }
}

export const personalization: Personalization = { ...(raw as Personalization) };
refreshPersonalization();
